/**
 * Billing → Plan → **Replace card** (plan-020 **WAPP-BILL**; design §32.6.6, §32.17 "Card replacement"; ED-23,
 * R-PAY-7; API-CARD's routes).
 *
 * A plan's card is always established by a NEW cardholder-present recurring charge on that card (a mandate exists only
 * after a captured charge — ED-10), so replacing it charges the new card once:
 *
 *   - a renewal waiting for the person (`past_due`, `suspended`): the charge IS this month's renewal payment — the
 *     plan is paid and moves to the new card in one step;
 *   - nothing due: the smallest credit pack, added to the balance as purchased credit.
 *
 * The amount is the server's (`POST /v1/billing/payment-methods/subscription-form` with `purpose: card_replacement`).
 * Before the card form opens the person agrees to the recurring terms in the plan's own words (Q-15, the consent box
 * UNTICKED, `RECURRING_CHARGE_CONSENT_TEXT_REVISION`) with how to cancel (Q-13); the processor's form asks for a mobile
 * number before Pay enables (Q-17). The token goes to `POST /v1/billing-subscriptions/{id}/card-replacement` under an
 * Idempotency-Key per card token, so a resend replays rather than charging twice. A 3-D Secure step sends the browser to
 * the processor; a refusal says the plan keeps its card.
 */
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { RECURRING_CHARGE_CONSENT_TEXT_REVISION } from "@wetalk/domain";
import { Button, Dialog } from "@wetalk/ui";
import { useEffect, useState, type ReactNode } from "react";

import { QUERY_KEY } from "../../api/query.js";
import { useGateway } from "../../api/provider.js";
import {
  openSubscriptionPayform,
  type SubscriptionCardFormView,
  type SubscriptionCardReplacementOutcomeView,
} from "../../api/slice/billing-operation.js";
import type { BalanceSubscriptionView } from "../../api/slice/subscription.js";
import { portalText, usePortalTranslation } from "../../i18n/portal.js";
import { formatMoney } from "../../money.js";
import { longDateText } from "../finance/plans/plan-copy.js";
import { RefusalLine } from "../finance/plans/plan-quote.js";
import { goToPaymentPage } from "./payment-page.js";
import { replacementAmountTextOf } from "./plan-billing.js";

const PAYFORM_ELEMENT_ID = "card-replacement-payform";

type Stage =
  | { readonly kind: "review" }
  | { readonly kind: "form" }
  | { readonly kind: "done"; readonly outcome: SubscriptionCardReplacementOutcomeView }
  | { readonly kind: "declined"; readonly message: string | null };

/** The replacement's Idempotency-Key: one per subscription and card token (16–128 characters, the API's rule). */
export function cardReplacementKeyOf(billingSubscriptionId: string, cardToken: string): string {
  return `card-replacement-${billingSubscriptionId}-${cardToken}`.slice(0, 128);
}

/**
 * The monthly amount the person agrees to (Q-15): the renewal the new card pays now, else the plan's next charge —
 * `null` when the plan will not be charged again (a cancellation scheduled), so there is nothing to agree to.
 */
export function consentTotalOf(form: Pick<SubscriptionCardFormView, "establishment" | "amount">, held: Pick<BalanceSubscriptionView, "nextCharge">): string | null {
  if (form.establishment === "renewal_payment") return formatMoney(held.nextCharge?.total ?? form.amount);
  return held.nextCharge === null ? null : formatMoney(held.nextCharge.total);
}

/** What the outcome says: the card the plan now uses, or that the plan could not be moved yet. */
export function replacementOutcomeTextOf(outcome: SubscriptionCardReplacementOutcomeView): string {
  if (!outcome.replaced) return portalText("billing", "card.not_moved");
  const lastFour = outcome.card?.lastFour ?? null;
  return lastFour === null ? portalText("billing", "card.done_plain") : portalText("billing", "card.done", { last4: lastFour });
}

export interface CardReplacementDialogProps {
  readonly open: boolean;
  readonly held: BalanceSubscriptionView;
  readonly onClose: () => void;
}

export function CardReplacementDialog(props: CardReplacementDialogProps): ReactNode {
  usePortalTranslation("billing");
  usePortalTranslation("subscription");
  const gateway = useGateway();
  const queryClient = useQueryClient();
  const { held } = props;
  const [consented, setConsented] = useState(false);
  const [stage, setStage] = useState<Stage>({ kind: "review" });

  const form = useQuery({
    queryKey: ["card-replacement-form", held.billingSubscriptionId] as const,
    queryFn: () => gateway.openSubscriptionCardForm({ purpose: "card_replacement", billingSubscriptionId: held.billingSubscriptionId }),
    enabled: props.open,
    staleTime: Number.POSITIVE_INFINITY,
    gcTime: 0,
    retry: false,
  });

  const replacing = useMutation({
    mutationFn: (cardToken: string) => {
      const opened = form.data;
      if (opened === undefined) throw new Error(portalText("billing", "card.preparing"));
      return gateway.replaceSubscriptionCard(held.billingSubscriptionId, {
        cardToken,
        consent: { textRevision: RECURRING_CHARGE_CONSENT_TEXT_REVISION, agreed: true },
        idempotencyKey: cardReplacementKeyOf(held.billingSubscriptionId, cardToken),
        billingQuoteId: opened.billingQuoteId,
        amountDueMinor: opened.amount.amountMinor,
      });
    },
    onSuccess: async (outcome) => {
      if (outcome.nextActionUrl !== null) {
        goToPaymentPage(outcome.nextActionUrl);
        return;
      }
      if (outcome.operationState === "failed" || outcome.operationState === "cancelled") {
        setStage({ kind: "declined", message: null });
        return;
      }
      setStage({ kind: "done", outcome });
      await queryClient.invalidateQueries({ queryKey: ["balance-subscription"] });
      await queryClient.invalidateQueries({ queryKey: QUERY_KEY.paymentMethod });
    },
  });

  const { mutate: replaceWith } = replacing;
  const opened = form.data;
  const formOpen = stage.kind === "form" && opened !== undefined ? opened : null;
  useEffect(() => {
    if (formOpen === null) return;
    let cancelled = false;
    openSubscriptionPayform(formOpen, PAYFORM_ELEMENT_ID).then(
      (cardToken) => {
        if (!cancelled) replaceWith(cardToken);
      },
      (error: unknown) => {
        if (!cancelled) setStage({ kind: "declined", message: error instanceof Error ? error.message : null });
      },
    );
    return () => {
      cancelled = true;
    };
  }, [formOpen, replaceWith]);

  const close = (): void => {
    setConsented(false);
    setStage({ kind: "review" });
    replacing.reset();
    props.onClose();
  };

  const consentTotal = opened === undefined ? null : consentTotalOf(opened, held);
  const nothingToReplace = opened !== undefined && consentTotal === null;
  return (
    <Dialog
      open={props.open}
      title={portalText("billing", "card.title")}
      onClose={close}
      actions={
        stage.kind === "done" ? (
          <Button variant="primary" onClick={close}>{portalText("billing", "pay.close")}</Button>
        ) : (
          <>
            <Button variant="secondary" disabled={replacing.isPending} onClick={close}>{portalText("billing", "card.keep")}</Button>
            {stage.kind === "declined" ? (
              <Button
                variant="primary"
                onClick={() => {
                  setConsented(false);
                  replacing.reset();
                  setStage({ kind: "review" });
                }}
              >
                {portalText("billing", "card.try_again")}
              </Button>
            ) : null}
            {stage.kind === "review" && !nothingToReplace ? (
              <Button variant="primary" disabled={opened === undefined || !consented} onClick={() => setStage({ kind: "form" })}>
                {portalText("billing", "card.continue")}
              </Button>
            ) : null}
          </>
        )
      }
    >
      {form.isPending && props.open ? <p className="c-note" aria-busy="true">{portalText("billing", "card.preparing")}</p> : null}
      {form.isError ? <RefusalLine error={form.error} otherwise={portalText("finance", "m0694")} /> : null}
      {opened !== undefined && stage.kind !== "done" ? (
        nothingToReplace ? (
          <p className="c-note">{portalText("billing", "card.nothing_to_replace", { period_end: held.period === null ? "" : longDateText(held.period.end, held.billingTimezone) })}</p>
        ) : (
          <>
            <p className="c-note">{replacementAmountTextOf(opened.establishment, formatMoney(opened.amount))}</p>
            <p className="c-note">{portalText("subscription", "quote.cancel_how")}</p>
            {stage.kind === "review" ? (
              <label className="bl-consent">
                <input type="checkbox" checked={consented} onChange={(event) => setConsented(event.currentTarget.checked)} />
                <span>{portalText("subscription", "quote.consent", { recurring_total: consentTotal ?? "" })}</span>
              </label>
            ) : null}
            {opened.phoneRequired ? <p className="c-note">{portalText("subscription", "quote.payform_note")}</p> : null}
          </>
        )
      ) : null}
      {stage.kind === "form" ? (
        <div className="bl-payform">
          <div id={PAYFORM_ELEMENT_ID} />
          {replacing.isPending ? <p className="c-note" role="status">{portalText("subscription", "quote.confirming")}</p> : null}
        </div>
      ) : null}
      {stage.kind === "done" ? (
        <p className="c-note" role="status">
          {stage.outcome.operationState === "completed" || stage.outcome.replaced ? replacementOutcomeTextOf(stage.outcome) : portalText("subscription", "quote.checking")}
        </p>
      ) : null}
      {stage.kind === "declined" ? <p className="bl-plan-error" role="alert">{stage.message ?? portalText("billing", "card.declined")}</p> : null}
      {replacing.isError ? <RefusalLine error={replacing.error} otherwise={portalText("finance", "m0694")} /> : null}
    </Dialog>
  );
}
